import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { User } from 'oidc-client-ts';
import { useAuth } from '../hooks/useAuth';
import { useStorageConfig } from '../contexts/StorageConfigContext';
import {
  requestDeviceCode,
  pollForToken,
  fetchUserInfo,
  type DeviceAuthorizationResponse,
} from '../utils/deviceFlow';

export interface DeviceLoginProps {
  /**
   * API base URL for device flow endpoints
   * @example 'http://localhost:5000'
   */
  apiBaseUrl: string;

  /**
   * QR code size in pixels
   * @default 256
   */
  qrCodeSize?: number;

  /**
   * Polling interval in seconds
   * @default 5
   */
  pollingInterval?: number;

  /**
   * Custom CSS class
   */
  className?: string;

  /**
   * Callback on successful login
   */
  onSuccess?: (user: User) => void;

  /**
   * Callback on error
   */
  onError?: (error: Error) => void;

  /**
   * Custom inline styles
   */
  style?: React.CSSProperties;
}

/**
 * Device flow login component
 *
 * Displays QR code and user code for device authentication. Automatically polls
 * for authorization and completes the login flow.
 *
 * @example Basic usage
 * ```tsx
 * import { DeviceLogin } from 'antaeus.keycloak.react';
 *
 * function TVLoginPage() {
 *   return (
 *     <DeviceLogin
 *       apiBaseUrl="http://localhost:5000"
 *       onSuccess={(user) => {
 *         console.log('Logged in:', user.profile.name);
 *         navigate('/home');
 *       }}
 *     />
 *   );
 * }
 * ```
 *
 * @example With custom settings
 * ```tsx
 * <DeviceLogin
 *   apiBaseUrl="http://localhost:5000"
 *   qrCodeSize={300}
 *   pollingInterval={3}
 *   onError={(error) => console.error('Login failed:', error)}
 * />
 * ```
 */
export const DeviceLogin: React.FC<DeviceLoginProps> = ({
  apiBaseUrl,
  qrCodeSize = 256,
  pollingInterval = 5,
  className = '',
  onSuccess,
  onError,
  style,
}) => {
  const auth = useAuth();
  const { storage, storageType } = useStorageConfig();
  const [deviceData, setDeviceData] = useState<DeviceAuthorizationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const pollTimerRef = useRef<number | null>(null);

  // Initialize device flow
  useEffect(() => {
    const initDeviceFlow = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await requestDeviceCode(apiBaseUrl);
        setDeviceData(response);
        setTimeRemaining(response.expires_in);
      } catch (err) {
        const error = err as Error;
        setError(error.message);
        onError?.(error);
      } finally {
        setLoading(false);
      }
    };

    initDeviceFlow();
  }, [apiBaseUrl, onError]);

  // Countdown timer
  useEffect(() => {
    if (!deviceData || timeRemaining === null) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          setError('Device code expired. Please refresh to try again.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [deviceData, timeRemaining]);

  // Poll for token
  useEffect(() => {
    if (!deviceData) return;

    const poll = async () => {
      try {
        const tokenResponse = await pollForToken(apiBaseUrl, deviceData.device_code);

        if (tokenResponse) {
          // Success! Store the token
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
          }

          // Fetch user info
          const userInfo = await fetchUserInfo(apiBaseUrl, tokenResponse.access_token);

          // Create User object with all required fields for proper storage
          const user = new User({
            id_token: tokenResponse.id_token || '',
            access_token: tokenResponse.access_token,
            token_type: tokenResponse.token_type || 'Bearer',
            profile: userInfo,
            expires_at: Math.floor(Date.now() / 1000) + tokenResponse.expires_in,
            scope: tokenResponse.scope,
            refresh_token: tokenResponse.refresh_token,
            session_state: null, // Device flow doesn't have session_state
          });

          console.log('[DeviceLogin] Device flow authentication successful', {
            hasAccessToken: !!user.access_token,
            hasRefreshToken: !!user.refresh_token,
            expiresAt: user.expires_at,
            profile: userInfo.preferred_username,
          });

          // Store the user manually in browser storage
          // We need to determine the storage key format used by oidc-client-ts
          const storageKey = `oidc.user:${auth.settings.authority}:${auth.settings.client_id}`;
          const userJson = user.toStorageString();

          console.log('[DeviceLogin] Storing user in storage...', {
            storageKey,
            storageType,
            storage: storageType === 'local' ? 'localStorage' : 'sessionStorage',
          });

          // Store the user using the configured storage
          storage.setItem(storageKey, userJson);

          // Verify storage worked
          const stored = storage.getItem(storageKey);
          if (!stored) {
            console.error('[DeviceLogin] Failed to store user in storage!');
            throw new Error('Failed to store user session. Please try again.');
          }
          console.log('[DeviceLogin] User stored successfully in', storageType === 'local' ? 'localStorage' : 'sessionStorage');

          // Trigger success callback if provided, otherwise reload the page
          if (onSuccess) {
            // Small delay to ensure storage write completes before navigation
            await new Promise(resolve => setTimeout(resolve, 100));
            onSuccess(user);
          } else {
            // No callback provided - reload the page to pick up the stored user
            // This ensures the auth context initializes with the user from storage
            window.location.reload();
          }
        }
      } catch (err) {
        // Ignore authorization_pending errors
        if (!(err as Error).message.includes('authorization_pending')) {
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
          }
          const error = err as Error;
          setError(error.message);
          onError?.(error);
        }
      }
    };

    // Start polling
    pollTimerRef.current = setInterval(poll, pollingInterval * 1000);

    // Cleanup
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, [deviceData, apiBaseUrl, pollingInterval, onSuccess, onError, auth]);

  if (loading) {
    return (
      <div className={`kc-device-login kc-device-login--loading ${className}`} style={style}>
        <div className="kc-device-login__spinner" />
        <p>Initializing device flow...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`kc-device-login kc-device-login--error ${className}`} style={style}>
        <div className="kc-device-login__error-icon">⚠️</div>
        <h2>Error</h2>
        <p>{error}</p>
        <button
          className="kc-button kc-button--primary"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!deviceData) {
    return null;
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`kc-device-login ${className}`} style={style}>
      <h2 className="kc-device-login__title">Device Login</h2>

      <div className="kc-device-login__qr">
        <QRCodeSVG value={deviceData.verification_uri_complete} size={qrCodeSize} />
      </div>

      <div className="kc-device-login__instructions">
        <p>Scan the QR code with your phone or visit:</p>
        <code className="kc-device-login__url">{deviceData.verification_uri}</code>
        <p>And enter code:</p>
        <strong className="kc-device-login__user-code">{deviceData.user_code}</strong>
      </div>

      {timeRemaining !== null && timeRemaining > 0 && (
        <p className="kc-device-login__expiry">
          Code expires in {formatTime(timeRemaining)}
        </p>
      )}

      <div className="kc-device-login__status">
        <div className="kc-device-login__spinner kc-device-login__spinner--small" />
        <p>Waiting for authorization...</p>
      </div>
    </div>
  );
};
